import {
  Component,
  EventEmitter,
  HostBinding,
  Output,
  ViewChildren,
  QueryList,
  ElementRef,
  effect,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ClippingStudioManagerService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-manager.service";
import {
  importedSubtitleTrackDisplayName,
  isImportedSubtitleTrackKey,
} from "app/js/src/infrastructure/clipping-studio/clipping-studio-media-subtitle-loader";
import { ClippingStudioTranscriptBlock, ClippingStudioSubtitleStyle } from "app/js/src/domain/clipping-studio/clipping-studio-transcript";
import { ClippingStudioSubtitleEvent } from "app/js/src/domain/clipping-studio/clipping-studio-project";
import { createInitialClippingStudioState } from "app/js/src/domain/clipping-studio/clipping-studio-state";

@Component({
  selector: "evrideo-clipping-studio-transcript-subtitles-panel",
  templateUrl: "./clipping-studio-transcript-subtitles-panel.component.html",
  styleUrls: ["./clipping-studio-transcript-subtitles-panel.component.scss"],
})
export class ClippingStudioTranscriptSubtitlesPanelComponent {
  readonly state = toSignal(this.manager.state$, {
    initialValue: createInitialClippingStudioState(),
  });

  activeTab: "transcript" | "subtitles" = "subtitles";
  transcriptSearch = "";
  subtitleSearch = "";
  showSubtitleStylePanel = false;
  showExportDropdown = false;

  @HostBinding("class.style-panel-open") get isStylePanelOpen(): boolean {
    return this.showSubtitleStylePanel;
  }

  /** Emits when the subtitle style panel open state changes (so parent can allow overflow). */
  @Output() stylePanelOpenChange = new EventEmitter<boolean>();

  @ViewChildren("subtitleBlockContainer", { read: ElementRef })
  subtitleBlockContainers!: QueryList<ElementRef>;

  constructor(private manager: ClippingStudioManagerService) {
    effect(() => {
      const s = this.state();
      const id = s?.selectedSubtitleId;
      if (id) {
        this.activeTab = "subtitles";
        setTimeout(() => this.scrollSelectedSubtitleIntoView(id), 150);
      }
    });
  }

  private scrollSelectedSubtitleIntoView(selectedSubtitleId: string): void {
    if (!this.subtitleBlockContainers?.length) return;
    const el = this.subtitleBlockContainers
      .toArray()
      .find(
        (ref) =>
          ref.nativeElement.getAttribute("data-subtitle-id") === selectedSubtitleId,
      );
    if (el) {
      el.nativeElement.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }

  /** Language Management rows (Subtitles tab). Only real tracks from state; no mock fallback. */
  get subtitleLanguageRows(): {
    languageName: string;
    displayName: string;
    subtitleLabel: string;
    hasSubs: boolean;
    isImported: boolean;
  }[] {
    const tracks = this.state()?.subtitleTracksByLanguage;
    if (!tracks || Object.keys(tracks).length === 0) {
      return [];
    }
    return Object.keys(tracks).map((lang) => {
      const importedName = importedSubtitleTrackDisplayName(lang);
      return {
        languageName: lang,
        displayName: importedName ?? lang,
        subtitleLabel: importedName ? "Imported" : "Subtitle",
        hasSubs: (tracks[lang]?.length ?? 0) > 0,
        isImported: isImportedSubtitleTrackKey(lang),
      };
    });
  }

  get filteredTranscriptBlocks(): ClippingStudioTranscriptBlock[] {
    const s = this.state();
    const tracks = s?.transcriptTracks ?? [];
    const activeLang = s?.activeTranscriptLanguage ?? "";
    const track = tracks.find((t) => t.language === activeLang);
    const blocks = track?.blocks ?? [];
    if (!this.transcriptSearch.trim()) return blocks;
    const term = this.transcriptSearch.trim().toLowerCase();
    return blocks.filter((b) => b.text.toLowerCase().includes(term));
  }

  get filteredSubtitles(): ClippingStudioSubtitleEvent[] {
    const subtitles = this.state()?.subtitles ?? [];
    if (!this.subtitleSearch.trim()) return subtitles;
    const term = this.subtitleSearch.trim().toLowerCase();
    return subtitles.filter((s) => s.Text.toLowerCase().includes(term));
  }

  isSubtitleSelected(sub: ClippingStudioSubtitleEvent): boolean {
    const id = this.state()?.selectedSubtitleId ?? null;
    return id != null && sub.id === id;
  }

  clearSubtitleSelection(): void {
    if (this.state()?.selectedSubtitleId == null) return;
    this.manager.setSelectedSubtitleId(null);
  }

  onSubtitleBlockSelect(id: string): void {
    if (this.state()?.selectedSubtitleId === id) {
      this.manager.setSelectedSubtitleId(null);
    } else {
      this.manager.setSelectedSubtitleId(id);
    }
  }

  setActiveTab(tab: "transcript" | "subtitles"): void {
    this.activeTab = tab;
  }

  setActiveTranscriptLanguage(lang: string): void {
    this.manager.setActiveTranscriptLanguage(lang);
  }

  async onRegenerate(): Promise<void> {
    await this.manager.regenerateTranscript();
  }

  /** Go to start of transcript line (seek only). */
  onGoToIn(ms: number): void {
    const seekMs =
      this.manager.state.mode === "EDIT" &&
      (this.manager.state.clips?.length ?? 0) > 0
        ? this.manager.sourceToEditTimeMs(ms)
        : ms;
    this.manager.seek(seekMs);
  }

  /** Go to end of transcript line (seek only). */
  onGoToOut(ms: number): void {
    const seekMs =
      this.manager.state.mode === "EDIT" &&
      (this.manager.state.clips?.length ?? 0) > 0
        ? this.manager.sourceToEditTimeMs(ms)
        : ms;
    this.manager.seek(seekMs);
  }

  async generateFromTranscript(): Promise<void> {
    await this.manager.generateSubtitlesFromTranscript();
  }

  addSubtitle(): void {
    const now =
      this.manager.state.mode === "EDIT" &&
      (this.manager.state.clips?.length ?? 0) > 0
        ? this.manager.getCurrentSourceTimeMs()
        : this.manager.state.currentTimeMs;
    const newSub: ClippingStudioSubtitleEvent = {
      id: `sub-${Date.now()}`,
      StartTimeMS: now,
      EndTimeMS: now + 3000,
      Text: "",
    };
    this.manager.addSubtitle(newSub);
  }

  onImportFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const content = reader.result as string;
      const format = file.name.toLowerCase().endsWith(".vtt") ? "vtt" : "srt";
      this.manager.importSubtitles(content, format);
    };
    reader.readAsText(file);
    input.value = "";
  }

  async exportSubs(format: "srt" | "vtt"): Promise<void> {
    this.showExportDropdown = false;
    const content = await this.manager.exportSubtitles(format);
    const blob = new Blob([content], {
      type: format === "vtt" ? "text/vtt" : "text/plain",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subtitles.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  }

  openExportDropdown(): void {
    this.showExportDropdown = true;
  }

  closeExportDropdown(): void {
    this.showExportDropdown = false;
  }

  onSubtitleTextChange(payload: { id: string; text: string }): void {
    this.manager.updateSubtitleText(payload.id, payload.text);
  }

  onSplitSubtitle(id: string): void {
    this.manager.splitSubtitle(id);
  }

  onDeleteSubtitle(id: string): void {
    this.manager.removeSubtitle(id);
  }

  openStylePanel(): void {
    this.showSubtitleStylePanel = true;
    this.stylePanelOpenChange.emit(true);
  }

  closeStylePanel(): void {
    this.showSubtitleStylePanel = false;
    this.stylePanelOpenChange.emit(false);
  }

  onApplySubtitleStyle(style: ClippingStudioSubtitleStyle): void {
    this.manager.applySubtitleStyle(style);
    this.showSubtitleStylePanel = false;
    this.stylePanelOpenChange.emit(false);
  }

  onSwitchSubtitleLanguage(lang: string): void {
    this.manager.setActiveSubtitleLanguage(lang);
  }

  onDeleteImportedTrack(trackKey: string): void {
    this.manager.removeImportedSubtitleTrack(trackKey);
  }

  /** Mock: generate subtitles for a language (no subs yet). */
  onGenerateSubsForLanguage(_languageName: string): void {
    // Stub: could call manager or show toast
  }

  /** Mock: open AI translate for subtitles. */
  onSubsAiTranslate(): void {
    // Stub: open modal or dropdown
  }
}
